import { getParameterUnitMetadata } from '../../../types/measurement';

/**
 * Maximum rows in one test session. Mirrors MAX_SESSION_ENTRIES of
 * POST /api/v1/aquariums/:aquariumId/measurements/session — keep them equal.
 */
export const MAX_ENTRIES = 20;

/**
 * Units a value can be entered in, per parameter; the first is the default. The
 * server converts these to the canonical unit (src/routes/measurementSession.ts)
 * and rejects any other unit for alkalinity, salinity and temperature.
 */
const ENTRY_UNITS: Readonly<Record<string, readonly string[]>> = {
  alkalinity: ['dKH', 'meq/L', 'ppm CaCO3'],
  salinity: ['ppt', 'SG'],
  temperature: ['°C', '°F'],
};

export function entryUnitsFor(parameterId: string): readonly string[] {
  const units = ENTRY_UNITS[parameterId];
  if (units) return units;
  // pH has no unit in PARAMETER_OPTIONS, but the server requires a non-empty one.
  return [getParameterUnitMetadata(parameterId)?.canonicalUnit || 'pH'];
}

/** The server's entry unit for a test-kit profile's unit (the catalogue stores salinity as 'sg'). */
export function profileEntryUnit(profileUnit: string): string {
  return profileUnit.toLowerCase() === 'sg' ? 'SG' : profileUnit;
}

/**
 * Soft typo check against a test-kit profile's plausible range (in the profile's
 * unit): a value outside the range whose ×10 or ÷10 falls inside it gets a
 * "did you mean …?" suggestion. Mirrors src/services/plausibilityCheck.ts.
 */
export function checkPlausibility(value: number, min: number, max: number): { suggestion: number } | null {
  if (!Number.isFinite(value) || value <= 0 || min > max) return null;
  if (value >= min && value <= max) return null;
  const divided = value / 10;
  if (divided >= min && divided <= max) return { suggestion: divided };
  const multiplied = value * 10;
  if (multiplied >= min && multiplied <= max) return { suggestion: multiplied };
  return null;
}

/** A `datetime-local` input value (local time, minutes) for a date. */
export function toLocalDateTimeInput(date: Date): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(
    date.getMinutes(),
  )}`;
}
